import { Activation, BetaTester, Checkout, Claim, CodeValueSuggestion, Credits, CreditsSummary, Device, Entitlement, EntitlementTransfer, Library, Organization, OrgRole, Sku, SkuPromotion, SkuVersion, SkuVersionStatus, SyncReport, License, LicenseType, NfaOrderStatus, NotificationPreferences, Offering, Order, Points, PointSpending, PointTopup, Product, ReconcileReport, Redemption, ResellerCode, GuestOrder, GuestOrderStats, SkuOrder, SkuOrderStats, ClaimedOrder, AutoLinkedIlok, RedemptionCodeBatch, RedemptionCodeLookup, Subscription, Topup, Trial, User, UserProfile, UserSearchResult, AlModel, Policy, PolicyKind } from "./models";

import { getFreshAccessToken, recoverFromUnauthorized } from "../auth/session";
import { maintenanceAwareFetch } from "./maintenance";

// Every call in this module goes through the maintenance-aware fetch: a
// 503/Maintenance from any route closes the app (see maintenance.ts). The
// global is shadowed on purpose so no call site needs to opt in.
const fetch = maintenanceAwareFetch

const apiUrl = process.env.REACT_APP_API_URL!;
const alProductId = process.env.REACT_APP_AL_PRODUCT_ID!;

// Non-2xx response, with the status kept so callers can tell a rejected
// token (401) from everything else.
export class ApiError extends Error {
  status: number
  errorCode?: string
  body?: unknown

  constructor(status: number, message: string, errorCode?: string, body?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.errorCode = errorCode
    this.body = body
  }
}

// `throw await apiError(response)` — tolerates a non-JSON body (gateway
// 401/403 pages) instead of masking the status behind a SyntaxError.
export async function apiError(response: Response): Promise<ApiError> {
  const body: any = await response.json().catch(() => ({}))
  const message = typeof body?.errorMessage === "string" ? body.errorMessage : `HTTP status: ${response.status}`
  console.error(body)
  return new ApiError(response.status, message, body?.errorCode, body)
}

// fetch against the API with the freshest session token. `accessToken` only
// says that the call is authenticated — the token actually sent comes from
// the session store (refreshed first when it is about to expire). A 401 on
// that token triggers one forced refresh and one retry; the API's lambda
// never runs before the JWT authorizer rejects, so re-sending is safe for
// every method.
export async function apiFetch(path: string, init: RequestInit = {}, accessToken?: string, retryOn401 = true): Promise<Response> {
  const sent = accessToken ? (await getFreshAccessToken().catch(() => undefined)) ?? accessToken : undefined
  const response = await fetch(apiUrl + path, {
    ...init,
    headers: {
      'Accept': 'application/json',
      ...(sent ? { 'Authorization': sent } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  })
  if (response.status === 401 && sent && retryOn401) {
    const rotated = await recoverFromUnauthorized(sent).catch(() => undefined)
    if (rotated && rotated !== sent)
      return apiFetch(path, init, rotated, false)
  }
  return response
}

export default class Api {

  // Legacy call sites pass the token they rendered with; the header carries
  // the freshest one the session store has (see apiFetch), the passed token
  // only as fallback. Empty/undefined = unauthenticated call.
  static async getHeaders(accessToken?: string): Promise<Record<string, string>> {
    if (accessToken) {
      const fresh = await getFreshAccessToken().catch(() => undefined)
      return {
        'Accept': 'application/json',
        'Authorization': fresh ?? accessToken,
      }
    } else {
      return {
        'Accept': 'application/json',
      }
    }
  }

  // Records consent to the CURRENT version of each document given (kind →
  // version); 409 when one of them changed meanwhile (reload and show the
  // new one).
  static async acceptPolicies(accessToken: string, versions: Partial<Record<PolicyKind, string>>): Promise<{ policies: Policy[] }> {
    const response = await fetch(apiUrl + "/me/policies/acceptances", {
      method: 'POST',
      headers: { ...(await this.getHeaders(accessToken)), 'Content-Type': 'application/json' },
      body: JSON.stringify({ versions }),
    });
    const json = await response.json().catch(() => ({}));
    if (response.status !== 200)
      throw new Error(json.errorMessage ?? `Could not record your acceptance (HTTP ${response.status})`);
    return json;
  }

  static async getMe(accessToken: string): Promise<{ user: User, subscription?: Subscription, policies: Policy[], claimedOrders: ClaimedOrder[], autoLinkedIlok: AutoLinkedIlok[] }> {
    const response = await apiFetch("/me", { method: 'GET', cache: 'default' }, accessToken);
    if (response.status !== 200)
      throw await apiError(response);
    const json = await response.json();
    return {
      user: json.user,
      // one entry per published document (empty = no gating)
      policies: json.policies ?? [],
      subscription: json.subscription,
      // guest purchases this very call materialized; absent = none
      claimedOrders: json.claimedOrders ?? [],
      // iLok accounts this very call linked on its own; absent = none
      autoLinkedIlok: json.autoLinkedIlok ?? [],
    }
  }

  static async getLicenses({ accessToken }: { accessToken: string }): Promise<{ licenses: License[], products: Product[], activations: Activation[], orders: Order[], skus: Sku[] }> {
    let url = apiUrl + "/me/licenses"
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // licenses: json.licenses.map((license: any) => ({
        //   ...license,
        //   createdAt: new Date(license.createdAt),
        //   expiresAt: license.expiresAt ? new Date(license.expiresAt) : undefined,
        //   nextActivationAt: license.nextActivationAt ? new Date(license.nextActivationAt) : undefined,
        // })),
        licenses: json.licenses,
        products: json.products,
        activations: json.activations,
        // activations: json.activations.map((activation: any) => ({
        //   ...activation,
        //   activatedAt: new Date(activation.activatedAt),
        //   expiresAt: activation.expiresAt ? new Date(activation.expiresAt) : null
        // })),
        orders: json.orders,
        skus: json.skus
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getActivation({ activationId, accessToken }: { activationId: string, accessToken: string }): Promise<{ activation: Activation }> {
    let url = apiUrl + "/me/activations/" + activationId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return json.activation
      // return {
      // activation: {
      //   ...json.activation,
      //   activatedAt: new Date(json.activation.activatedAt),
      //   expiresAt: json.activation.expiresAt ? new Date(json.activation.expiresAt) : null
      // }
      // }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }


  static async revokeActivation(
    { activationId, accessToken }: { activationId: string, accessToken: string }
  ): Promise<{ activation: Activation, license: License }> {
    let url = apiUrl + "/me/activations/" + activationId
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
      // return {
      // activation: {
      //   ...json.activation,
      //   activatedAt: new Date(json.activation.activatedAt),
      //   expiresAt: json.activation.expiresAt ? new Date(json.activation.expiresAt) : null
      // }
      // }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async patchDevice(
    { deviceId, deviceName, accessToken }: { deviceId: string, deviceName: string, accessToken: string }
  ): Promise<{ device: Device }> {
    let url = apiUrl + "/me/devices/" + deviceId
    const response = await fetch(url, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ deviceName })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getNotificationPreferences(
    { accessToken }: { accessToken: string }
  ): Promise<NotificationPreferences> {
    const response = await fetch(apiUrl + "/me/notification-preferences", {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return json.notificationPreferences
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async patchNotificationPreferences(
    { updates, accessToken }: { updates: NotificationPreferences, accessToken: string }
  ): Promise<NotificationPreferences> {
    const response = await fetch(apiUrl + "/me/notification-preferences", {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(updates)
    });
    if (response.status === 200) {
      const json = await response.json();
      return json.notificationPreferences
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getLibrary({ accessToken }: { accessToken: string }): Promise<Library> {
    let url = apiUrl + "/me/library"
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default'
    });
    if (response.status === 200) {
      return await response.json() as Library
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getLicense({ licenseId, accessToken }: { licenseId: string, accessToken: string }): Promise<{ license: License, product: Product, activations: Activation[], order?: Order, skus: Sku[] }> {
    let url = apiUrl + "/me/licenses/" + licenseId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // license: {
        //   ...json.license,
        //   createdAt: new Date(json.license.createdAt),
        //   expiresAt: json.license.expiresAt ? new Date(json.license.expiresAt) : undefined,
        //   nextActivationAt: json.license.nextActivationAt ? new Date(json.license.nextActivationAt) : undefined,
        // },
        license: json.license,
        product: json.product,
        // activations: json.activations.map((activation: any) => ({
        //   ...activation,
        //   activatedAt: new Date(activation.activatedAt),
        //   expiresAt: activation.expiresAt ? new Date(activation.expiresAt) : null
        // })),
        activations: json.activations,
        order: json.order,
        skus: json.skus
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Subscription skus offered for sale + the user's extra trial days
  // (GET /me/store/skus?paymentType=subscription — the former GET /me/plans).
  static async getActivePlans(accessToken: string): Promise<{ extraTrialDays?: { productId: string, days: number }[], skus: Sku[], claims: Claim[], products: Product[] }> {
    // the endpoint paginates (default 25) — every subscription tier must fit
    // on one max page for the tier cards
    let url = apiUrl + "/me/store/skus?" + new URLSearchParams({ paymentType: "subscription", count: "100" })
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { extraTrialDays?: { productId: string, days: number }[], skus: Sku[], claims: Claim[], products: Product[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The Store page catalog: one-time, free AND reward skus (subscriptions
  // live on the subscription page). search matches name/description/
  // highlights; the price range is in cents against the effective money
  // price (free skus count as 0, reward skus price in points and drop out);
  // shape narrows by license-claim count (bundle = several, single = one).
  static async listStoreSkus({ accessToken, search, sellerId, minPriceCents, maxPriceCents, shape, link, count }: { accessToken: string, search?: string, sellerId?: string, minPriceCents?: number, maxPriceCents?: number, shape?: "bundle" | "single", link?: string, count?: number }): Promise<{ skus: Sku[], claims: Claim[], products: Product[], link: string | null }> {
    const params = new URLSearchParams({ paymentType: "one_time,free,reward" })
    if (search) params.set("search", search)
    if (sellerId) params.set("sellerId", sellerId)
    if (minPriceCents !== undefined) params.set("minPriceCents", String(minPriceCents))
    if (maxPriceCents !== undefined) params.set("maxPriceCents", String(maxPriceCents))
    if (shape) params.set("shape", shape)
    if (link) params.set("link", link)
    if (count) params.set("count", String(count))
    let url = apiUrl + "/me/store/skus?" + params
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { skus: Sku[], claims: Claim[], products: Product[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getMySku({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ sku: Sku, claims: Claim[], products: Product[] }> {
    let url = apiUrl + "/me/store/skus/" + skuId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { sku: Sku, claims: Claim[], products: Product[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Presigns an image PUT for an EXISTING sku — its screenshot, or with
  // kind "logo" its (normalized) logo, whose minimum width is lower
  // (128px). The key is derived server-side from the sku's owner and id
  // (<ownerId>/skus/<skuId>/images/<rev>/...), which is why Add SKU creates
  // the sku first; the image scaler generates the smaller variants after
  // the upload. The rev must be echoed back (imageRev / logoRev) on
  // updateSku.
  static async createSkuImageUpload({ skuId, width, kind, accessToken }: { skuId: string, width: number, kind?: "screenshot" | "logo", accessToken: string }): Promise<{ uploadUrl: string, imageUrl: string, rev: string }> {
    const response = await fetch(apiUrl + "/admin/skus/uploads", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ skuId, width, ...(kind ? { kind } : {}) })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Presigns an installer PUT for the version dialogs (the sku must exist —
  // its owner and id determine the key:
  // <ownerId>/skus/<skuId>/installers/<version>/<rev>/<fileName>). The PUT must send
  // Content-Type: application/octet-stream (baked into the signature).
  static async createSkuInstallerUpload({ skuId, version, platform, fileName, accessToken }: {
    skuId: string, version: string, platform: "osx" | "windows", fileName: string, accessToken: string
  }): Promise<{ uploadUrl: string, downloadUrl: string, rev: string }> {
    const response = await fetch(apiUrl + "/admin/skus/installer-uploads", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ skuId, version, platform, fileName })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Creates an unshipped, versionless sku — identity/metadata only (the
  // deliberately minimal Add SKU step), without images: those are keyed by
  // the sku's id, so they follow via createSkuImageUpload + updateSku. Its
  // first draft version, with the claim set and installers, is created
  // afterwards via createSkuVersion. ownerId is Admin-only. (The endpoint
  // still accepts version/installers/claims for one-call creation — this
  // client deliberately doesn't send them.)
  static async createSku({ sku, accessToken }: {
    sku: {
      name: string, description: string, highlights: string[],
      codePrefix?: string, ownerId?: string
    },
    accessToken: string
  }): Promise<{ sku: Sku, claims: Claim[], products: Product[] }> {
    const response = await fetch(apiUrl + "/admin/skus", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(sku)
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The admin view of one sku — the only fetch carrying codePrefix and the
  // offerings' storeVariantId. Admin any sku, Developer only their own.
  static async getAdminSku({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ sku: Sku }> {
    const response = await fetch(apiUrl + "/admin/skus/" + skuId, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { sku: Sku }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Partial update of the sku's editable surface. The name is freely
  // editable (uploads are keyed by the sku's id). imageWidth: null clears
  // the screenshot; a number replaces it and requires the imageRev minted
  // by createSkuImageUpload for that upload (logoWidth/logoRev likewise for
  // the logo).
  static async updateSku({ skuId, patch, accessToken }: {
    skuId: string,
    patch: {
      name?: string, description?: string, highlights?: string[], codePrefix?: string | null,
      imageWidth?: number | null, imageRev?: string,
      logoWidth?: number | null, logoRev?: string
    },
    accessToken: string
  }): Promise<{ sku: Sku }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(patch)
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async createOffering({ skuId, offering, accessToken }: {
    skuId: string,
    offering: { paymentType: "one_time" | "subscription" | "free" | "reward", priceCents?: number, storeProductId?: string, storeVariantId?: string, rewardPoints?: number, intervalUnit?: "day" | "week" | "month" | "year", intervalCount?: number, trialDays?: number, licenseExpiryDays?: number, dynamicPricing?: "percent" | "amount" | "fixed" | null, dynamicPriceCents?: number | null, discounts?: { productId: string, discount: number | null }[], upgradePricing?: "value" | "count" },
    accessToken: string
  }): Promise<{ offering: Offering }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/offerings`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(offering)
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Partial update; paymentType is immutable server-side. discontinued: false
  // restores a soft-deleted offering (the only accepted value — discontinuing
  // goes through deleteOffering).
  static async updateOffering({ skuId, offeringId, patch, accessToken }: {
    skuId: string,
    offeringId: string,
    patch: { priceCents?: number, storeProductId?: string | null, storeVariantId?: string | null, rewardPoints?: number, intervalUnit?: "day" | "week" | "month" | "year" | null, intervalCount?: number | null, trialDays?: number | null, licenseExpiryDays?: number | null, dynamicPricing?: "percent" | "amount" | "fixed" | null, dynamicPriceCents?: number | null, discounts?: { productId: string, discount: number | null }[], upgradePricing?: "value" | "count", discontinued?: false },
    accessToken: string
  }): Promise<{ offering: Offering }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/offerings/${offeringId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(patch)
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // deleted: false = the offering was marked discontinued instead (historical
  // orders/subscriptions reference it); 409 when active subscriptions do.
  static async deleteOffering({ skuId, offeringId, accessToken }: { skuId: string, offeringId: string, accessToken: string }): Promise<{ deleted: boolean, offering: Offering | null }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/offerings/${offeringId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  /* Promotions (admin SKU page): a sale on the sku's one-time offering —
     a percent off XOR the promoted price, over a window (endsAt null =
     open-ended). Only one may run at a time (409 on overlap). */

  static async listPromotions({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ promotions: SkuPromotion[] }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/promotions`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async createPromotion({ skuId, promotion, accessToken }: {
    skuId: string,
    promotion: { name: string | null, discountPercent?: number | null, priceCents?: number | null, startsAt: string, endsAt: string | null },
    accessToken: string
  }): Promise<{ promotion: SkuPromotion }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/promotions`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(promotion)
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Partial update; an ended promotion is immutable (409). Sending either
  // discount key replaces the pair. { endsAt: <now> } ends a running one.
  static async updatePromotion({ skuId, promotionId, patch, accessToken }: {
    skuId: string,
    promotionId: string,
    patch: { name?: string | null, discountPercent?: number | null, priceCents?: number | null, startsAt?: string, endsAt?: string | null },
    accessToken: string
  }): Promise<{ promotion: SkuPromotion }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/promotions/${promotionId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(patch)
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // scheduled or running only — an ended promotion is kept as history (409)
  static async deletePromotion({ skuId, promotionId, accessToken }: { skuId: string, promotionId: string, accessToken: string }): Promise<{ deleted: boolean }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/promotions/${promotionId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // A sku's releases for the signed-in user, newest first — requires a
  // license for a product the sku claims (the Licenses-page "Older versions"
  // dialog).
  static async listMySkuVersions({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ versions: SkuVersion[] }> {
    const response = await fetch(`${apiUrl}/me/skus/${skuId}/versions`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Every release of a sku, newest first. Admin any sku, Developer their own.
  static async listSkuVersions({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ versions: SkuVersion[] }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/versions`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Creates a version — as a draft (default) or released directly. `claims`
  // is the version's exact set (omitted = copy of the latest released's, or
  // the newest row while nothing is released). The number must be greater
  // than the latest released version and unique among all rows. For a direct
  // release, installers for both platforms are required once the sku ships
  // products and the backend reconciles right after (retroactive license
  // claims deposit to owners of older versions immediately, reported in
  // `reconcile` — null for drafts). A released version's URLs immediately
  // become the sku's download links everywhere.
  static async createSkuVersion({ skuId, version, accessToken }: {
    skuId: string,
    version: {
      // installers omitted = draft, or a credits-only release (the backend
      // rejects that for released versions of skus that ship products)
      version: string, status?: SkuVersionStatus, downloadMacUrl?: string, downloadWinUrl?: string,
      claims?: (
        | { type: "license", productId: string, maxActivations: number, maxYearlyActivations?: number, retroactive: boolean }
        | { type: "topup", productId: string, amount: number, expiryDays?: number }
      )[]
    },
    accessToken: string
  }): Promise<{ version: SkuVersion, reconcile: ReconcileReport | null }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/versions`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(version)
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Edits an unreleased version (released rows are immutable): renumber,
  // swap installers, redefine the claim set, or toggle betaAutoDeposit.
  // Claim/number edits re-sync the version's holders' licenses (`sync`);
  // toggling betaAutoDeposit on while in beta deposits to the current tester
  // list (`reconcile`).
  static async patchSkuVersion({ skuId, versionId, patch, accessToken }: {
    skuId: string,
    versionId: string,
    patch: {
      version?: string, downloadMacUrl?: string | null, downloadWinUrl?: string | null,
      betaAutoDeposit?: boolean,
      claims?: (
        | { type: "license", productId: string, maxActivations: number, maxYearlyActivations?: number, retroactive: boolean }
        | { type: "topup", productId: string, amount: number, expiryDays?: number }
      )[]
    },
    accessToken: string
  }): Promise<{ version: SkuVersion, sync: SyncReport | null, reconcile: ReconcileReport | null }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/versions/${versionId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(patch)
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The forward-only lifecycle transition (draft→alpha→beta→released, stages
  // may be skipped). To alpha: alpha licenses are deposited to every member
  // of the owning organization. To beta: betaAutoDeposit deposits beta
  // licenses to the whole tester list now and to testers added later. To
  // released: reconcile runs (retroactive claims light up for older owners),
  // the version becomes the sku's fulfillment target, and alphaLicenses/
  // betaLicenses decide the team's/testers' holdings first, per test type —
  // keep (default), convert to standard/nfr, or remove.
  static async postSkuVersionStatus({ skuId, versionId, status, betaAutoDeposit, alphaLicenses, betaLicenses, accessToken }: {
    skuId: string,
    versionId: string,
    status: SkuVersionStatus,
    betaAutoDeposit?: boolean,
    alphaLicenses?: "keep" | "remove" | "standard" | "nfr",
    betaLicenses?: "keep" | "remove" | "standard" | "nfr",
    accessToken: string
  }): Promise<{
    version: SkuVersion,
    reconcile: ReconcileReport | null,
    alphaLicenses?: { action: string, entitlements: number },
    betaLicenses?: { action: string, entitlements: number },
    // beta entitlements revoked at release because their holders also hold
    // alpha ones (member + tester overlap) — the team's license wins
    duplicateBetaLicensesRemoved?: number
  }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/versions/${versionId}/status`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({
        status,
        ...(betaAutoDeposit !== undefined ? { betaAutoDeposit } : {}),
        ...(alphaLicenses !== undefined ? { alphaLicenses } : {}),
        ...(betaLicenses !== undefined ? { betaLicenses } : {})
      })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Deletes a version. Released: only the latest released and only while
  // nothing references it. Unreleased: any position — their test entitlements
  // and licenses are destroyed with them.
  static async deleteSkuVersion({ skuId, versionId, accessToken }: { skuId: string, versionId: string, accessToken: string }): Promise<{ deleted: boolean }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/versions/${versionId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  /* ORGANIZATIONS (vendors) — the ownership anchor. The id is the permanent
     handle/upload namespace, the name the display face. */

  static async listOrganizations({ accessToken }: { accessToken: string }): Promise<{ organizations: Organization[] }> {
    const response = await fetch(`${apiUrl}/admin/organizations`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // System admin only — onboards a vendor; the handle is permanent.
  // websiteUrl and the logo pair (imageWidth+imageRev, from
  // createOrganizationImageUpload) are optional.
  static async createOrganization({ id, name, websiteUrl, imageWidth, imageRev, accessToken }: {
    id: string, name: string, websiteUrl?: string, imageWidth?: number, imageRev?: string, accessToken: string
  }): Promise<{ organization: Organization }> {
    const response = await fetch(`${apiUrl}/admin/organizations`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ id, name, websiteUrl, imageWidth, imageRev })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Edit the display face (name, websiteUrl, logo; the handle is permanent).
  // Partial: absent fields keep their value, explicit nulls clear websiteUrl
  // / the logo. System admin or the org's vendor admin.
  static async updateOrganization({ orgId, name, websiteUrl, imageWidth, imageRev, accessToken }: {
    orgId: string, name?: string, websiteUrl?: string | null, imageWidth?: number | null, imageRev?: string, accessToken: string
  }): Promise<{ organization: Organization }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ name, websiteUrl, imageWidth, imageRev })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Presigned PUT for an organization logo (the sku-image conventions; the
  // echoed rev goes back on create/update as imageRev). Vendor admin of an
  // existing org; a not-yet-created orgId is the create flow (system admin).
  static async createOrganizationImageUpload({ orgId, width, accessToken }: { orgId: string, width: number, accessToken: string }): Promise<{ uploadUrl: string, imageUrl: string, rev: string }> {
    const response = await fetch(`${apiUrl}/admin/organizations/uploads`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ orgId, width })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Vendor admin of the org (or system admin) only.
  static async listOrganizationMembers({ orgId, accessToken }: { orgId: string, accessToken: string }): Promise<{ members: { userId: string, orgRole: OrgRole, createdAt: string, user: UserSearchResult }[] }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}/members`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The new member is deposited alpha licenses for every org version
  // currently in team testing (alpha or beta) — one reconcile per version.
  static async addOrganizationMember({ orgId, userId, orgRole, accessToken }: { orgId: string, userId: string, orgRole: OrgRole, accessToken: string }): Promise<{ member: { userId: string, orgRole: OrgRole }, reconciles: ReconcileReport[] }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}/members`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ userId, orgRole })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Role change; the last vendor admin can't be demoted.
  static async updateOrganizationMember({ orgId, userId, orgRole, accessToken }: { orgId: string, userId: string, orgRole: OrgRole, accessToken: string }): Promise<{ member: { userId: string, orgRole: OrgRole } }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}/members/${userId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ orgRole })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The last vendor admin can't be removed. Removal also revokes the user's
  // alpha licenses in the org's running alphas/betas (revokedCount).
  static async removeOrganizationMember({ orgId, userId, accessToken }: { orgId: string, userId: string, accessToken: string }): Promise<{ removed: boolean, revokedCount: number }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  /* BETA TESTERS — the ORGANIZATION's list, nested under the org like
     member management and gated the same way (org admin or system admin). */

  static async listBetaTesters({ orgId, accessToken }: { orgId: string, accessToken: string }): Promise<{ testers: BetaTester[] }> {
    const url = `${apiUrl}/admin/organizations/${orgId}/beta-testers`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Adds an existing user to the list; they are auto-deposited into every
  // beta version of the org's with the auto-deposit flag on.
  static async addBetaTester({ orgId, userId, accessToken }: { orgId: string, userId: string, accessToken: string }): Promise<{ tester: { userId: string, createdAt: string }, reconciles: ReconcileReport[] }> {
    const response = await fetch(`${apiUrl}/admin/organizations/${orgId}/beta-testers`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ userId })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Removes a user from the list AND revokes their beta licenses in the
  // org's currently running betas (kept licenses from finished betas stay).
  static async removeBetaTester({ orgId, userId, accessToken }: { orgId: string, userId: string, accessToken: string }): Promise<{ removed: boolean, revokedCount: number }> {
    const url = `${apiUrl}/admin/organizations/${orgId}/beta-testers/${userId}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // paymentType "shipping" aggregates every shipped kind; shape narrows by
  // license-claim count (bundle = several, single = exactly one)
  static async getSkus({ accessToken, search, sellerId, paymentType, shape, link, count }: { accessToken: string, search?: string, sellerId?: string, paymentType?: "one_time" | "subscription" | "free" | "reward" | "not_shipped" | "shipping", shape?: "bundle" | "single", link?: string, count?: number }): Promise<{ skus: Sku[], claims: Claim[], products: Product[], link: string | null }> {
    const params = new URLSearchParams()
    if (search) params.set("search", search)
    if (sellerId) params.set("sellerId", sellerId)
    if (paymentType) params.set("paymentType", paymentType)
    if (shape) params.set("shape", shape)
    if (link) params.set("link", link)
    if (count) params.set("count", String(count))
    const query = params.toString()
    let url = apiUrl + "/admin/skus" + (query ? "?" + query : "")
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { skus: Sku[], claims: Claim[], products: Product[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getSku({ skuId }: { skuId: string }): Promise<{ sku: Sku, claims: Claim[], products: Product[] }> {
    let url = apiUrl + "/store/skus/" + skuId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { sku: Sku, claims: Claim[], products: Product[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getClaim({ claimId }: { claimId: string }): Promise<{ claim: Claim, product?: Product }> {
    let url = apiUrl + "/store/claims/" + claimId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { claim: Claim, product?: Product }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getRedemptionCodeClaims({ redemptionCode, accessToken }: { redemptionCode: string, accessToken: string }): Promise<{ claims: Claim[], products: Product[], rewardPoints: number }> {
    let url = apiUrl + "/me/claims?code=" + redemptionCode
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { claims: Claim[], products: Product[], rewardPoints: number }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getSkuClaims({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ claims: Claim[], products: Product[] }> {
    let url = apiUrl + `/admin/skus/${skuId}/claims`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { claims: Claim[], products: Product[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Permanently deletes a sku (with its claims and versions). 409 with a
  // message listing the blockers while anything references it — owners,
  // orders, subscriptions, codes, reward purchases or offerings.
  static async deleteSku({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ deleted: boolean }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getSubscription({ accessToken }: { accessToken: string }): Promise<{ subscription?: Subscription, sku?: Sku, claims: Claim[], products: Product[] }> {
    let url = apiUrl + "/me/subscription"
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // subscription: json.subscription ? {
        //   ...json.subscription,
        //   renewsAt: new Date(json.subscription.renewsAt),
        //   endsAt: json.subscription.endsAt ? new Date(json.subscription.endsAt) : null
        // } : undefined,
        subscription: json.subscription,
        sku: json.sku,
        claims: json.claims,
        products: json.products
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async patchSubscription({ accessToken, offeringId }: { accessToken: string, offeringId: string }): Promise<{ subscription: Subscription, sku: Sku, claims: Claim[], products: Product[] }> {
    let url = apiUrl + "/me/subscription"
    const response = await fetch(url, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ offeringId })
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // subscription: {
        //   ...json.subscription,
        //   renewsAt: new Date(json.subscription.renewsAt),
        //   endsAt: json.subscription.endsAt ? new Date(json.subscription.endsAt) : null
        // },
        subscription: json.subscription,
        sku: json.sku,
        claims: json.claims,
        products: json.products
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async deleteSubscription({ accessToken }: { accessToken: string }): Promise<{ subscription: Subscription }> {
    let url = apiUrl + "/me/subscription"
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // subscription: {
        //   ...json.subscription,
        //   renewsAt: new Date(json.subscription.renewsAt),
        //   endsAt: json.subscription.endsAt ? new Date(json.subscription.endsAt) : null
        // }
        subscription: json.subscription
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async putSubscription({ accessToken }: { accessToken: string }): Promise<{ subscription: Subscription }> {
    let url = apiUrl + "/me/subscription"
    const response = await fetch(url, {
      method: 'PUT',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // subscription: {
        //   ...json.subscription,
        //   renewsAt: new Date(json.subscription.renewsAt),
        //   endsAt: json.subscription.endsAt ? new Date(json.subscription.endsAt) : null
        // }
        subscription: json.subscription
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getCredits({ accessToken }: { accessToken: string }): Promise<{ credits: Credits }> {
    let url = apiUrl + "/me/credits?productId=" + alProductId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { credits: Credits }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getCreditsSummary({ accessToken }: { accessToken: string }): Promise<{ summary: CreditsSummary }> {
    let url = apiUrl + `/me/credits/summary?productId=${alProductId}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { summary: CreditsSummary }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getTopup({ id, accessToken }: { id: string, accessToken: string }): Promise<{ topup: Topup }> {
    let url = apiUrl + `/me/topups/${id}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json()
      return {
        // topup: {
        //   ...json.topup,
        //   createdAt: new Date(json.topup.createdAt),
        //   expiresAt: json.topup.expiresAt ? new Date(json.topup.expiresAt) : null
        // }
        topup: json.topup
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listTopups({ includeExpired, link, count, accessToken }: { includeExpired: boolean, link: string | null, count: number, accessToken: string }): Promise<{ topups: Topup[], link: string | null }> {
    let url = apiUrl + `/me/topups?expired=${includeExpired ? "1" : "0"}&productId=${alProductId}&count=${count}${link ? `&link=${link}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json()
      return {
        // topups: json.topups.map((topup: any) => ({
        //   ...topup,
        //   createdAt: new Date(topup.createdAt),
        //   expiresAt: topup.expiresAt ? new Date(topup.expiresAt) : null
        // })),
        topups: json.topups,
        link: json.link
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getPoints({ accessToken }: { accessToken: string }): Promise<{ points: Points }> {
    let url = apiUrl + "/me/points"
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { points: Points }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getPointTopup({ id, accessToken }: { id: string, accessToken: string }): Promise<{ pointTopup: PointTopup }> {
    let url = apiUrl + `/me/points/topups/${id}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { pointTopup: PointTopup }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listPointTopups({ includeExpired, link, count, accessToken }: { includeExpired: boolean, link: string | null, count: number, accessToken: string }): Promise<{ pointTopups: PointTopup[], link: string | null }> {
    let url = apiUrl + `/me/points/topups?expired=${includeExpired ? "1" : "0"}&count=${count}${link ? `&link=${link}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { pointTopups: PointTopup[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // total/totalUsers arrive only on the first page (link === null) and only
  // once the backend ships them — treat as optional
  static async listEntitlements({ skuId, userId, origin, status, link, count, accessToken }: { skuId?: string | null, userId?: string | null, origin?: string | null, status?: string | null, link: string | null, count: number, accessToken: string }): Promise<{ entitlements: Entitlement[], link: string | null, total?: number, totalUsers?: number }> {
    let url = apiUrl + `/admin/entitlements?count=${count}${skuId ? `&skuId=${skuId}` : ""}${userId ? `&userId=${encodeURIComponent(userId)}` : ""}${origin ? `&origin=${origin}` : ""}${status ? `&status=${status}` : ""}${link ? `&link=${link}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { entitlements: Entitlement[], link: string | null, total?: number, totalUsers?: number }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Any user's subscription for the admin detail page; developers may only
  // fetch subscriptions of skus they own
  static async adminGetSubscription({ subscriptionId, accessToken }: { subscriptionId: string, accessToken: string }): Promise<{ subscription: Subscription }> {
    const url = apiUrl + "/admin/subscriptions/" + subscriptionId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { subscription: Subscription }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // One user's deposited licenses for the admin surface; developers get only
  // the slice on products their skus claim (server-side scoping). skuId is the
  // sku the license came through — absent for trial licenses.
  static async adminGetUserLicenses({ userId, accessToken }: { userId: string, accessToken: string }): Promise<{ licenses: (License & { skuId?: string })[], products: Product[] }> {
    const url = apiUrl + `/admin/licenses?userId=${encodeURIComponent(userId)}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { licenses: (License & { skuId?: string })[], products: Product[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async revokeEntitlement({ entitlementId, reason, accessToken }: { entitlementId: string, reason: string | null, accessToken: string }): Promise<{ entitlement: Entitlement, deletedLicenseIds: string[] }> {
    const url = apiUrl + `/admin/entitlements/${entitlementId}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      // reason is optional — the endpoint accepts a bodyless DELETE
      ...(reason ? { body: JSON.stringify({ reason }) } : {}),
    });
    if (response.status === 200) {
      return await response.json() as { entitlement: Entitlement, deletedLicenseIds: string[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Presigned upload slot for the caller's own avatar. The client PUTs the
  // JPEG to uploadUrl (Content-Type: image/jpeg, see uploadAvatar in
  // utils/image.ts), then writes imageUrl to the Cognito picture attribute.
  static async createAvatarUpload({ accessToken }: { accessToken: string }): Promise<{ uploadUrl: string, imageUrl: string }> {
    const response = await fetch(apiUrl + "/me/avatar/uploads", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
    });
    if (response.status === 200) {
      return await response.json() as { uploadUrl: string, imageUrl: string }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async setUserAccess({ userId, enabled, accessToken }: { userId: string, enabled: boolean, accessToken: string }): Promise<{ user: User }> {
    const url = apiUrl + `/admin/users/${userId}`
    const response = await fetch(url, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      body: JSON.stringify({ enabled }),
    });
    if (response.status === 200) {
      return await response.json() as { user: User }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Admin-only: a user's linked and pending iLok accounts (pending rows'
  // addresses masked, as on the user's own list).
  static async getUserIlokAccounts({ userId, accessToken }: { userId: string, accessToken: string }): Promise<{
    ilokAccounts: { ilokId: string, ilokEmail: string, confirmed: boolean, emailSentAt: string | null }[],
  }> {
    const url = `${apiUrl}/admin/users/${userId}/ilok-accounts`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Admin-only: unlinks an iLok account from a user — revokes the
  // entitlements (and their licenses) the link issued, revokes on PACE the
  // licenses deposited into the account, and deletes the link.
  static async unlinkUserIlokAccount({ userId, ilokId, accessToken }: { userId: string, ilokId: string, accessToken: string }): Promise<{
    revokedEntitlementIds: string[], deletedLicenseIds: string[], revokedIlokLicenseGuids: string[],
  }> {
    const url = `${apiUrl}/admin/users/${userId}/ilok-accounts/${encodeURIComponent(ilokId)}`
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listUsers({ link, count, accessToken }: { link: string | null, count: number, accessToken: string }): Promise<{ users: UserSearchResult[], link: string | null }> {
    // link is Cognito's PaginationToken — always URI-encode it
    let url = apiUrl + `/admin/users?count=${count}${link ? `&link=${encodeURIComponent(link)}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { users: UserSearchResult[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async searchUsers({ search, accessToken }: { search: string, accessToken: string }): Promise<{ users: UserSearchResult[] }> {
    let url = apiUrl + `/admin/users?search=${encodeURIComponent(search)}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { users: UserSearchResult[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getPointSpending({ id, accessToken }: { id: string, accessToken: string }): Promise<{ pointSpending: PointSpending }> {
    let url = apiUrl + `/me/points/spendings/${id}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { pointSpending: PointSpending }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listPointSpendings({ link, count, accessToken }: { link: string | null, count: number, accessToken: string }): Promise<{ pointSpendings: PointSpending[], link: string | null }> {
    let url = apiUrl + `/me/points/spendings?count=${count}${link ? `&link=${link}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { pointSpendings: PointSpending[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getProducts({ accessToken, search, ownerId, needsCredits, link, count }: { accessToken: string, search?: string, ownerId?: string, needsCredits?: boolean, link?: string, count?: number }): Promise<{ products: Product[], skus: Sku[], link: string | null }> {
    const params = new URLSearchParams()
    if (search) params.set("search", search)
    if (ownerId) params.set("ownerId", ownerId)
    if (needsCredits) params.set("needsCredits", "true")
    if (link) params.set("link", link)
    if (count) params.set("count", String(count))
    const query = params.toString()
    let url = apiUrl + "/admin/products" + (query ? "?" + query : "")
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { products: Product[], skus: Sku[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Presigns an image PUT for an EXISTING product — its screenshot, or
  // with kind "logo" its (normalized) logo, whose minimum width is lower
  // (128px). The key is derived server-side from the product's owner and id
  // (<ownerId>/products/<productId>/images/<rev>/...), which is why Add
  // Product creates the product first; the image scaler generates the
  // smaller variants after the upload. The rev must be echoed back
  // (imageRev / logoRev) on updateProduct.
  static async createProductImageUpload({ productId, width, kind, accessToken }: { productId: string, width: number, kind?: "screenshot" | "logo", accessToken: string }): Promise<{ uploadUrl: string, imageUrl: string, rev: string }> {
    const response = await fetch(apiUrl + "/admin/products/uploads", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ productId, width, ...(kind ? { kind } : {}) })
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Creates a product — a plain one, with no AI-assistant support (AL
  // wiring stays SQL-managed) and without images: those are keyed by the
  // product's id, so they follow via createProductImageUpload +
  // updateProduct. ownerId is Admin/multi-org only.
  static async createProduct({ product, accessToken }: {
    product: {
      // trial fields: absent = the backend's standard trial (14 days / 2
      // activations), explicit null = no trial
      name: string, ownerId?: string, url?: string, valueCents?: number,
      needsCredits?: boolean, trialDays?: number | null, trialMaxActivations?: number | null
    },
    accessToken: string
  }): Promise<{ product: Product }> {
    const response = await fetch(apiUrl + "/admin/products", {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(product)
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Partial update of the product's editable surface — owner is immutable
  // (it's the upload namespace) and the AL config is not editable here.
  // Explicit nulls clear url/valueCents/trials; imageWidth: null clears the
  // screenshot, a number replaces it and requires the imageRev minted by
  // createProductImageUpload for that upload (logoWidth/logoRev likewise
  // for the logo).
  static async updateProduct({ productId, patch, accessToken }: {
    productId: string,
    patch: {
      name?: string, url?: string | null, valueCents?: number | null,
      needsCredits?: boolean, trialDays?: number | null, trialMaxActivations?: number | null,
      imageWidth?: number | null, imageRev?: string,
      logoWidth?: number | null, logoRev?: string
    },
    accessToken: string
  }): Promise<{ product: Product }> {
    const response = await fetch(`${apiUrl}/admin/products/${productId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify(patch)
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Paginated licenses of one product (id-cursor, newest first); skuId is
  // the sku the license came through (absent for trials), total only on the
  // first page. Developers may only fetch their own products' licenses.
  static async adminGetProductLicenses({ productId, link, count, accessToken }: { productId: string, link?: string, count?: number, accessToken: string }): Promise<{ licenses: (License & { skuId?: string })[], total: number | null, link: string | null }> {
    const params = new URLSearchParams()
    if (link) params.set("link", link)
    if (count) params.set("count", String(count))
    const query = params.toString()
    const response = await fetch(apiUrl + "/admin/products/" + productId + "/licenses" + (query ? "?" + query : ""), {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Paginated trials of one product — same conventions as the licenses
  // listing above.
  static async adminGetProductTrials({ productId, link, count, accessToken }: { productId: string, link?: string, count?: number, accessToken: string }): Promise<{ trials: Trial[], total: number | null, link: string | null }> {
    const params = new URLSearchParams()
    if (link) params.set("link", link)
    if (count) params.set("count", String(count))
    const query = params.toString()
    const response = await fetch(apiUrl + "/admin/products/" + productId + "/trials" + (query ? "?" + query : ""), {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Deletes a product nothing references — the backend 409s with the list
  // of blockers (SKU claims, licenses, trials, credits history, A.L. usage)
  // and that message is surfaced to the dialog. Vendor-admin/system-admin.
  static async deleteProduct({ productId, accessToken }: { productId: string, accessToken: string }): Promise<{ deleted: boolean }> {
    const response = await fetch(`${apiUrl}/admin/products/${productId}`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Admin product detail; developers may only fetch their own products.
  // skus = the latest RELEASED version claims the product; upcomingSkus =
  // only an unreleased latest version does (invisible on the store).
  // alModels (absent for non-A.L. products) resolves the AL config's model
  // ids to names/providers.
  static async adminGetProduct({ productId, accessToken }: { productId: string, accessToken: string }): Promise<{ product: Product, skus: Sku[], upcomingSkus: Sku[], licenseCount: number, alModels?: { orchestrator: AlModel | null, worker: AlModel | null, description: AlModel | null } }> {
    const url = apiUrl + "/admin/products/" + productId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getProduct({ productId, accessToken }: { productId: string, accessToken: string }): Promise<{ product: Product, skus: Sku[] }> {
    let url = apiUrl + "/store/products/" + productId
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json()
      return {
        product: json.product,
        skus: json.skus
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // static async getOrders({ skuId, accessToken }: { skuId?: string, accessToken: string }): Promise<ApiOrder> {
  //   let url = apiUrl + "/me/orders/"
  //   if (skuId !== undefined)
  //     url += `?sku_id=${skuId}`
  //   const response = await fetch(url, {
  //     method: 'GET',
  //     headers: await this.getHeaders(accessToken),
  //     cache: 'default',
  //   });
  //   if (response.status === 200) {
  //     return (await response.json()).data;
  //   } else {
  //     throw Error("HTTP status: " + response.status);
  //   }
  // }

  static async listOrders({ skuId, status, purchaseId, accessToken }: { skuId?: string, status?: NfaOrderStatus, purchaseId?: string, accessToken: string }): Promise<{ orders: Order[] }> {
    const params = new URLSearchParams()
    if (skuId !== undefined)
      params.set("skuId", skuId)
    if (status !== undefined)
      params.set("status", status)
    if (purchaseId !== undefined)
      params.set("purchaseId", purchaseId)
    const query = params.toString()
    let url = apiUrl + "/me/orders" + (query ? "?" + query : "")
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return (await response.json()) as { orders: Order[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Tries the caller's own order first; on 403 (someone else's order,
  // reached e.g. via an entitlement's origin link) retries the admin surface,
  // which only succeeds for privileged users.
  static async getOrder({ orderId, accessToken }: { orderId: string, accessToken: string }): Promise<{ order: Order }> {
    for (const surface of ["me", "admin"]) {
      const url = apiUrl + "/" + surface + "/orders/" + orderId
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getHeaders(accessToken),
        cache: 'default',
      });
      if (response.status === 200) {
        return (await response.json()) as { order: Order }
      }
      if (response.status === 403 && surface === "me")
        continue
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
    throw Error("unreachable")
  }

  // POST /me/orders/{id}/refund — full self-service refund inside the
  // 14-day window; the response carries the refunded order
  static async refundOrder({ orderId, accessToken }: { orderId: string, accessToken: string }): Promise<{ order: Order }> {
    const response = await fetch(`${apiUrl}/me/orders/${orderId}/refund`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
    });
    if (response.status === 200) {
      return await response.json() as { order: Order }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // auth
  // static async getMeOrders({ skuId, status, accessToken }: { skuId?: string, status?: string, accessToken: string }): Promise<any> {
  //   let url = apiUrl + "/me/orders";
  //   let conditions = [];
  //   if (skuId)
  //     conditions.push(`sku_id=${skuId}`)
  //   if (status)
  //     conditions.push(`status=${status}`);
  //   if (conditions.length > 0)
  //     url += `?${conditions.join("&")}`;
  //   const response = await fetch(url, {
  //     method: 'GET',
  //     headers: await this.getHeaders(accessToken),
  //     cache: 'default',
  //   });
  //   if (response.status === 200) {
  //     return await response.json();
  //   } else {
  //     throw Error("HTTP status: " + response.status);
  //   }
  // }

  static async getUser({ userId, accessToken }: { userId: string, accessToken: string }): Promise<{ user: User }> {
    const url = `${apiUrl}/admin/users/${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      const json = await response.json();
      return {
        // user: {
        //   ...json.user,
        //   createdAt: new Date(json.user.createdAt),
        //   updatedAt: new Date(json.user.updatedAt)
        // }
        user: json.user
      }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Public profiles of every user with at least one shipped sku — the
  // storefront's manufacturer list (GET /me/store/sellers).
  static async listSellers({ accessToken }: { accessToken: string }): Promise<{ sellers: UserProfile[] }> {
    const response = await fetch(apiUrl + "/me/store/sellers", {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { sellers: UserProfile[] }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Public profile (name + avatar) of any user, readable by every signed-in
  // user — unlike getUser, which returns the full admin-only record.
  // Throws ApiError (status kept: 404 = no such account, which the transfer
  // dialog tells apart from a failure)
  static async getUserProfile({ userId, accessToken }: { userId: string, accessToken: string }): Promise<{ user: UserProfile }> {
    const response = await apiFetch(`/me/users/${encodeURIComponent(userId)}`, { method: 'GET', cache: 'default' }, accessToken)
    if (response.status !== 200)
      throw await apiError(response)
    return await response.json() as { user: UserProfile }
  }

  // Single checkout endpoint for both payment types: the backend dispatches on
  // the offering's paymentType. upgrade/purchaseId apply to one-time offerings
  // only; extraTrialDays applies to subscription offerings only.
  static async makeSkuCheckout({
    skuId,
    offeringId,
    redirectUrl,
    upgrade,
    crossgrade,
    promotionId,
    purchaseId,
    extraTrialDays,
    accessToken
  }: {
    skuId: string,
    offeringId: string,
    redirectUrl: string,
    upgrade?: {
      priceCents: number
      revokedLicenseIds: string[]
      consumedEntitlementIds: string[]
    } | null,
    // the crossgrade price shown (Sku.crossgrade) — the server recomputes and
    // rejects a stale one; mutually exclusive with upgrade
    crossgrade?: { priceCents: number } | null,
    // the sale shown (Sku.promotion.id) — an ended one is rejected so the
    // page refetches instead of charging a different price
    promotionId?: string | null,
    purchaseId?: string,
    extraTrialDays?: number,
    accessToken: string
  }): Promise<{ checkout: Checkout }> {
    let url = apiUrl + "/me/store/skus/" + skuId + "/checkouts?" + new URLSearchParams(purchaseId ? { redirectUrl, purchaseId } : { redirectUrl }).toString()
    const body: { offeringId: string, upgrade?: object, crossgrade?: object, promotionId?: string, extraTrialDays?: number } = { offeringId }
    if (upgrade) body.upgrade = upgrade
    if (crossgrade) body.crossgrade = crossgrade
    if (promotionId) body.promotionId = promotionId
    if (extraTrialDays) body.extraTrialDays = extraTrialDays
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
    })
    if (response.status === 201) {
      return (await response.json()) as { checkout: Checkout }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listRedemptions({ link, count, accessToken }: { link: string | null, count: number, accessToken: string }): Promise<{
    redemptions: Redemption[],
    link: string | null,
    skus: Sku[],
    licenses: License[],
    topups: Topup[],
    pointTopups: PointTopup[],
    products: Product[],
    activations: Activation[],
    entitlements: Entitlement[]
  }> {
    const url = `${apiUrl}/me/redemptions?count=${count}${link ? `&link=${link}` : ""}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getRedemption({ id, accessToken }: { id: string, accessToken: string }): Promise<{
    redemption: Redemption,
    sku: Sku,
    licenses: License[],
    topups: Topup[],
    pointTopups: PointTopup[],
    products: Product[],
    activations: Activation[],
    entitlements: Entitlement[]
  }> {
    // own redemption first; on 403 retry the admin surface (see getOrder)
    let response = await fetch(`${apiUrl}/me/redemptions/${id}`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 403) {
      response = await fetch(`${apiUrl}/admin/redemptions/${id}`, {
        method: 'GET',
        headers: await this.getHeaders(accessToken),
        cache: 'default',
      });
    }
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // skuVersionId picks which version the batch grants; omitted = the latest
  // expirationDays = the redemption window; licenseExpiryDays = the lifetime
  // of the granted entitlement (omitted = permanent)
  // codeValueCents = what one code is worth (omitted = the server's
  // suggestion, see getCodeValueSuggestion; ignored for non-standard types)
  // ilokEnabled: whether redeemed entitlements may get an iLok counterpart
  // (absent = yes; only meaningful for orgs with iLok switched on)
  static async generateCodes({ skuId, count, expirationDays, licenseExpiryDays, rewardPoints, licenseType, comment, skuVersionId, codeValueCents, ilokEnabled, accessToken }: { skuId: string, count: number, expirationDays?: number, licenseExpiryDays?: number, rewardPoints?: number, licenseType: LicenseType, comment?: string, skuVersionId?: string, codeValueCents?: number, ilokEnabled?: boolean, accessToken: string }): Promise<{ codes: string[] }> {
    const url = `${apiUrl}/admin/skus/${skuId}/codes`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ count, expirationDays, licenseExpiryDays, rewardPoints, licenseType, comment, skuVersionId, codeValueCents, ilokEnabled })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Grants a user the whole sku at the chosen license type: an entitlement
  // (origin=grant) plus the claim licenses, deposited immediately
  // ilokEnabled: whether the grantee may request an iLok counterpart
  // (absent = yes; only meaningful for orgs with iLok switched on)
  // licenseExpiryDays: the grant's lifetime; absent = perpetual
  static async grantSku({ skuId, userId, licenseType, ilokEnabled, licenseExpiryDays, accessToken }: {
    skuId: string,
    userId: string,
    licenseType: LicenseType,
    ilokEnabled?: boolean,
    licenseExpiryDays?: number,
    accessToken: string
  }): Promise<{ entitlement: Entitlement, reconcile: ReconcileReport }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/grants`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ userId, licenseType, ilokEnabled, licenseExpiryDays })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // The value a new standard code batch would be seeded with
  static async getCodeValueSuggestion({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<CodeValueSuggestion> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/code-value`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as CodeValueSuggestion
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listCodeBatches({ skuId, link, count, accessToken }: { skuId: string, link: string | null, count: number, accessToken: string }): Promise<{ batches: RedemptionCodeBatch[], link: string | null }> {
    const url = `${apiUrl}/admin/skus/${skuId}/code-batches?count=${count}${link ? `&link=${link}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { batches: RedemptionCodeBatch[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Looks one redemption code up by its value (dashes and case forgiven) within
  // a sku; null when no such code exists for the sku. Unredeemed codes resolve
  // too — this is the admin's "what's the status of this code" read.
  static async lookupRedemptionCode({ skuId, code, accessToken }: { skuId: string, code: string, accessToken: string }): Promise<RedemptionCodeLookup | null> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/codes?code=${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as RedemptionCodeLookup
    } else if (response.status === 404) {
      return null
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Revokes a code batch: its unredeemed codes stop redeeming (e.g. the codes
  // leaked); already-redeemed grants are untouched. Undone by unrevokeCodeBatch.
  static async revokeCodeBatch({ skuId, batchId, reason, accessToken }: { skuId: string, batchId: string, reason: string | null, accessToken: string }): Promise<{ batch: RedemptionCodeBatch }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/code-batches/${batchId}/revoke`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      // reason is optional — the endpoint accepts a bodyless POST
      ...(reason ? { body: JSON.stringify({ reason }) } : {}),
    });
    if (response.status === 200) {
      return await response.json() as { batch: RedemptionCodeBatch }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Guest orders of a sku (store purchases without an account, parked until
  // the buyer's email claims them), newest first. q narrows to an email
  // prefix, the claiming user's id or the LS order number.
  // Admin: the sku's store orders, newest first. q narrows to a user id, an
  // email prefix (resolved to accounts server-side) or the LS order number.
  static async listSkuOrders({ skuId, q, link, count, accessToken }: { skuId: string, q: string | null, link: string | null, count: number, accessToken: string }): Promise<{ orders: SkuOrder[], link: string | null }> {
    const url = `${apiUrl}/admin/skus/${skuId}/orders?count=${count}${link ? `&link=${link}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { orders: SkuOrder[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getSkuOrderStats({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ stats: SkuOrderStats }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/orders/stats`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { stats: SkuOrderStats }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async listGuestOrders({ skuId, q, link, count, accessToken }: { skuId: string, q: string | null, link: string | null, count: number, accessToken: string }): Promise<{ guestOrders: GuestOrder[], link: string | null }> {
    const url = `${apiUrl}/admin/skus/${skuId}/guest-orders?count=${count}${link ? `&link=${link}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { guestOrders: GuestOrder[], link: string | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getGuestOrderStats({ skuId, accessToken }: { skuId: string, accessToken: string }): Promise<{ stats: GuestOrderStats }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/guest-orders/stats`, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json() as { stats: GuestOrderStats }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Support correction of a PENDING guest order: re-keys it to the address
  // the buyer meant (materializes on that account's next sign-in) and, by
  // default, tells the new address what to do. accountExists hints whether
  // an account already carries the address.
  static async amendGuestOrderEmail({ skuId, guestOrderId, email, resendEmail, accessToken }: { skuId: string, guestOrderId: string, email: string, resendEmail: boolean, accessToken: string }): Promise<{ guestOrder: GuestOrder, emailSent: boolean, accountExists: boolean | null }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/guest-orders/${guestOrderId}`, {
      method: 'PATCH',
      headers: await this.getHeaders(accessToken),
      body: JSON.stringify({ email, resendEmail }),
    });
    if (response.status === 200) {
      return await response.json() as { guestOrder: GuestOrder, emailSent: boolean, accountExists: boolean | null }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Hands a PENDING guest order to a chosen account — what the buyer's own
  // sign-in would do, with the account picked by the admin.
  static async depositGuestOrder({ skuId, guestOrderId, userId, accessToken }: { skuId: string, guestOrderId: string, userId: string, accessToken: string }): Promise<{ guestOrder: GuestOrder }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/guest-orders/${guestOrderId}/deposit`, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      body: JSON.stringify({ userId }),
    });
    if (response.status === 200) {
      return await response.json() as { guestOrder: GuestOrder }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async unrevokeCodeBatch({ skuId, batchId, accessToken }: { skuId: string, batchId: string, accessToken: string }): Promise<{ batch: RedemptionCodeBatch }> {
    const response = await fetch(`${apiUrl}/admin/skus/${skuId}/code-batches/${batchId}/revoke`, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
    });
    if (response.status === 200) {
      return await response.json() as { batch: RedemptionCodeBatch }
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async redeemCode({ code, accessToken }: { code: string, accessToken: string }): Promise<{
    redemption: Redemption,
    skus: Sku[],
    licenses: License[],
    topups: Topup[],
    pointTopups: PointTopup[],
    products: Product[],
    entitlements: Entitlement[]
  }> {
    const url = `${apiUrl}/me/redemptions`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ code })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Reseller-distributed codes (the reseller pages): what redeeming `code`
  // handed out by `reseller` would deposit — 404 unknown code, 400 already
  // redeemed (the message is the server's)
  static async getResellerCode({ reseller, code, accessToken }: { reseller: string, code: string, accessToken: string }): Promise<{
    code: ResellerCode,
    sku: Sku,
    claims: Claim[],
    products: Product[]
  }> {
    const response = await apiFetch(`/me/resellers/${encodeURIComponent(reseller)}/codes?code=${encodeURIComponent(code)}`, { method: 'GET' }, accessToken)
    if (response.status !== 200)
      throw await apiError(response)
    return await response.json()
  }

  // The same side-loaded shape as redeemCode (minus the redemption row), so
  // the slices absorb a reseller redemption the same way
  static async redeemResellerCode({ reseller, code, accessToken }: { reseller: string, code: string, accessToken: string }): Promise<{
    code: ResellerCode,
    skus: Sku[],
    licenses: License[],
    topups: Topup[],
    pointTopups: PointTopup[],
    products: Product[],
    activations: Activation[],
    entitlements: Entitlement[]
  }> {
    const response = await apiFetch(`/me/resellers/${encodeURIComponent(reseller)}/redemptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code })
    }, accessToken)
    if (response.status !== 201)
      throw await apiError(response)
    return await response.json()
  }

  // The user's linked and pending iLok accounts (a pending row's address is
  // masked). Accounts registered to the user's email are linked by the
  // backend on the app's first load (/me), so there is nothing to suggest.
  static async getIlokAccounts({ accessToken }: { accessToken: string }): Promise<{
    ilokAccounts: { ilokId: string, ilokEmail: string, confirmed: boolean, emailSentAt: string | null }[],
  }> {
    const url = `${apiUrl}/me/ilok-accounts`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Cancels a link whose verification email is still out (the user's own
  // pending row).
  static async cancelIlokLink({ ilokId, accessToken }: { ilokId: string, accessToken: string }): Promise<void> {
    const url = `${apiUrl}/me/ilok-accounts/${encodeURIComponent(ilokId)}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status !== 204) {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Starts linking an iLok account from its User ID alone: the backend
  // looks it up on PACE and either links it outright (`linked`, its email
  // is this account's verified address; `issued` licenses just landed) or
  // emails a verification link there (`email`, masked).
  static async linkIlokAccount({ ilokId, accessToken }: { ilokId: string, accessToken: string }): Promise<{
    linked?: boolean,
    issued?: number,
    email?: string,
    alreadyLinked?: boolean,
  }> {
    const url = `${apiUrl}/me/ilok-accounts`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ ilokId })
    });
    // 201 = linked or verification email sent; 200 = already linked to this account (no-op)
    if (response.status === 201 || response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getIlokLicenses({ accessToken }: { accessToken: string }): Promise<{
    ilokLicenses: {
      ilokId: string, licenseGuid: string, productGuid: string, productName: string,
      licenseType: string, status: string, eligible: boolean,
      sku: { id: string, name: string, imageUrl: string | null } | null,
      claimed: boolean, deposited: boolean,
    }[],
  }> {
    const url = `${apiUrl}/me/ilok-licenses`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async getIlokDeposits({ accessToken }: { accessToken: string }): Promise<{
    deposits: { entitlementId: string, ilokId: string, ilokLicenseGuid: string | null, revokedAt: string | null }[],
    bindings: { entitlementId: string, ilokId: string, revokedAt: string | null }[],
    depositableSkuIds: string[],
    linkedIlokIds: string[],
  }> {
    const url = `${apiUrl}/me/ilok-deposits`;
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
    });
    if (response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  static async requestIlokDeposit({ entitlementId, ilokId, accessToken }: { entitlementId: string, ilokId: string, accessToken: string }): Promise<{
    licenseGuid?: string,
    alreadyDeposited?: boolean,
  }> {
    const url = `${apiUrl}/me/ilok-deposits`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ entitlementId, ilokId })
    });
    // 201 = deposited; 200 = already deposited earlier (no-op)
    if (response.status === 201 || response.status === 200) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }

  // Hands one of the user's entitlements to another account (irreversible).
  // movedLicenseIds are the sender's license rows that changed owner — the
  // store evicts them.
  static async transferEntitlement({ entitlementId, toUserId, accessToken }: { entitlementId: string, toUserId: string, accessToken: string }): Promise<{
    transfer: EntitlementTransfer,
    movedLicenseIds: string[]
  }> {
    const response = await apiFetch(`/me/entitlements/${encodeURIComponent(entitlementId)}/transfers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toUserId })
    }, accessToken)
    if (response.status !== 201)
      throw await apiError(response)
    return await response.json()
  }

  static async claimReward({ skuId, offeringId, accessToken }: { skuId: string, offeringId: string, accessToken: string }): Promise<{
    pointSpending: PointSpending,
    licenses: License[],
    topups: Topup[],
    products: Product[],
  }> {
    const url = `${apiUrl}/me/store/skus/${skuId}/rewards`;
    const response = await fetch(url, {
      method: 'POST',
      headers: await this.getHeaders(accessToken),
      cache: 'default',
      body: JSON.stringify({ offeringId })
    });
    if (response.status === 201) {
      return await response.json()
    } else {
      const error = await response.json()
      console.error(error)
      throw Error(error.errorMessage ?? ("HTTP status: " + response.status + "\n" + JSON.stringify(error)));
    }
  }
}
